import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataProviderInstance } from '../../../api/DataProvider';
import { GetCompanySetupStatusAPI } from '../../../api/API';
import { getAccessToken } from '../../../api/authTokens';
import { useSubscription } from '../../../contexts/SubscriptionContext';

// O webhook do Stripe pode chegar alguns segundos depois do retorno do checkout:
// enquanto a assinatura não aparece ativa, consulta de novo antes de montar o resumo
const SUBSCRIPTION_RETRY_ATTEMPTS = 5;
const SUBSCRIPTION_RETRY_DELAY_MS = 2000;

/**
 * Dados da volta do checkout do Stripe: força o refresh da assinatura, espera o
 * webhook ativar o plano (até SUBSCRIPTION_RETRY_ATTEMPTS consultas), monta o
 * resumo do que foi contratado e manda quem está no onboarding para o assistente.
 */
const useCheckoutSuccess = () => {
  const navigate = useNavigate();
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [setupStatus, setSetupStatus] = useState(null);
  
  // Get DataProvider functions
  const { GetCompanySubscription } = DataProviderInstance();
  
  // Get subscription context functions
  const { invalidateCache, refreshSubscription } = useSubscription();
  
  useEffect(() => {
    const fetchData = async (attempt = 1) => {
      try {
        setIsLoading(true);
        
        // Verificar se o token existe
        const token = getAccessToken();
        if (!token) {
          
          // Implementar mecanismo de retry - tentar novamente após um curto período
          setTimeout(() => {
            const newToken = getAccessToken();
            if (newToken) {
              
              fetchData(); // Chamar a função novamente com o novo token
            } else {
              setError('Erro de autenticação. Por favor, faça login novamente.');
              setIsLoading(false);
            }
          }, 1000); // Esperar 1 segundo antes de tentar novamente
          return;
        }
        
        // Invalidar o cache da assinatura pois acabamos de confirmar uma nova assinatura
        invalidateCache();
        
        // Forçar atualização dos dados da assinatura
        refreshSubscription(true);
        
        // Buscar os dados da assinatura atual
        GetCompanySubscription((result) => {
          if (result.success && result.subscription) {
            const subscription = result.subscription;

            if (!subscription.has_active_plan && attempt < SUBSCRIPTION_RETRY_ATTEMPTS) {
              setTimeout(() => fetchData(attempt + 1), SUBSCRIPTION_RETRY_DELAY_MS);
              return;
            }

            // Resumo do que acabou de ser contratado
            const formattedData = {
              has_active_plan: !!subscription.has_active_plan,
              plan_name: subscription.plan_name || null,
              price_cents: subscription.price_cents || 0,
              next_billing_date: subscription.next_due_date_formatted || null,
              trial_ends_at: subscription.trial_ends_at || null
            };
            
            setSessionData(formattedData);
            
            // Buscar o status do onboarding
            const sessionData = localStorage.getItem('sessionData');
            let companyId = null;
            
            if (sessionData) {
              try {
                const parsedSession = JSON.parse(sessionData);
                companyId = parsedSession.currentCompany;
                
                if (companyId != null && companyId != undefined) {
                  GetCompanySetupStatusAPI((statusData) => {
                    if (statusData && !statusData.error) {
                      setSetupStatus(statusData);
                    }
                    setIsLoading(false);
                  }, companyId);
                } else {
                  setIsLoading(false);
                }
              } catch (e) {
                
                setIsLoading(false);
              }
            } else {
              setIsLoading(false);
            }
          } else if (result.error && result.error.includes('401')) {
            // Erro de autenticação - tentar novamente após um curto período
            
            setTimeout(() => {
              fetchData(); // Tentar novamente
            }, 1500);
          } else {
            // Mesmo sem dados específicos, não mostraremos erro
            // Apenas exibiremos uma mensagem genérica de sucesso
            setIsLoading(false);
          }
        });
      } catch (err) {
        
        // Não mostraremos erro para o usuário, apenas log para diagnóstico
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [GetCompanySubscription]);
  
  // Efeito para redirecionar automaticamente quando o status do onboarding for carregado
  useEffect(() => {
    // Verificar se o setupStatus foi carregado e não estamos mais em loading
    if (setupStatus && !isLoading && !error) {
      // Se o usuário for novo (status INITIAL ou IN_PROGRESS), redirecionar automaticamente para o onboarding
      if (setupStatus.status === 'INITIAL' || setupStatus.status === 'IN_PROGRESS') {
        
        navigate('/setup-wizard');
      }
    }
  }, [setupStatus, isLoading, error, navigate]);

  return { sessionData, isLoading, error, setupStatus };
};

export default useCheckoutSuccess;
